import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Tabs } from '@antimatterhq/ui/components/tabs';
import {
  activeQueryTabIdAtom,
  queryTabAtomFamily,
  closeQueryTabAtom,
  switchTabAtom,
  updateTabAtomFamily,
  allQueryTabsAtom,
} from '@/atoms/query/queryAtoms';
import { useQueryStatus } from '@/hooks/components/query/useQueryStatus';

export function QueryTabItem({
  tabId,
  tabIndex,
}: {
  tabId: string;
  tabIndex: number;
}) {
  const tab = useAtomValue(queryTabAtomFamily(tabId));
  const activeTabId = useAtomValue(activeQueryTabIdAtom);
  const allTabs = useAtomValue(allQueryTabsAtom);
  const closeTab = useSetAtom(closeQueryTabAtom);
  const switchTab = useSetAtom(switchTabAtom);
  const updateTab = useSetAtom(updateTabAtomFamily(tabId));

  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  useQueryStatus({ tabId });

  // temporary progress indicator, this is a hack to show a progress indicator
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [refetchInterval, setRefetchInterval] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    if (tab?.status === 'pending') {
      setProgress(1);
      if (!refetchInterval) {
        setRefetchInterval(
          window.setInterval(() => {
            setProgress((prev) => (prev ? Math.min(prev + 10, 90) : 10));
          }, 500),
        );
      }
      return;
    }

    if (tab?.status === 'running') {
      return;
    }

    if (tab?.status === 'success') {
      setProgress(100);
      if (refetchInterval) {
        window.clearInterval(refetchInterval);
        setRefetchInterval(undefined);
      }
      return;
    }
    if (tab?.status === 'error') {
      if (refetchInterval) {
        window.clearInterval(refetchInterval);
        setRefetchInterval(undefined);
      }
      setProgress(undefined);
      return;
    }

    setProgress(undefined);
  }, [tab?.status, refetchInterval]);

  const isActive = tabId === activeTabId;

  // Get all tab names to check for duplicates
  const allTabNames = useMemo(() => {
    const names = new Map<string, string>();
    allTabs.forEach((tabData) => {
      if (tabData.id !== tabId) {
        names.set(tabData.name.toLowerCase(), tabData.id);
      }
    });
    return names;
  }, [allTabs, tabId]);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setLocalValue(tab?.name || '');
  }, [tab?.name]);

  const handleCommit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleBlur = useCallback(() => {
    const valueToSave = localValue.trim() || tab?.name || '';

    // Check for duplicate names
    if (allTabNames.has(valueToSave.toLowerCase())) {
      setShowDuplicateWarning(true);
      // Don't save, keep editing
      return;
    }

    updateTab({ name: valueToSave });
    setLocalValue(valueToSave);
    setShowDuplicateWarning(false);
    handleCommit();
  }, [localValue, tab?.name, updateTab, handleCommit, allTabNames]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        const valueToSave = localValue.trim() || tab?.name || '';

        // Check for duplicate names
        if (allTabNames.has(valueToSave.toLowerCase())) {
          setShowDuplicateWarning(true);
          // Don't save, keep editing
          return;
        }

        updateTab({ name: valueToSave });
        setLocalValue(valueToSave);
        setShowDuplicateWarning(false);
        handleCommit();
      } else if (e.key === 'Escape') {
        setLocalValue(tab?.name || '');
        setShowDuplicateWarning(false);
        handleCommit();
      }
    },
    [localValue, tab?.name, updateTab, handleCommit, allTabNames],
  );

  const handleSelect = useCallback(() => {
    if (!isActive) {
      switchTab(tabId);
    } else if (!isEditing) {
      handleStartEdit();
    }
  }, [isActive, isEditing, switchTab, tabId, handleStartEdit]);

  const handleCloseTab = useCallback(() => {
    closeTab(tabId);
  }, [closeTab, tabId]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      // Clear warning as user types
      if (showDuplicateWarning) {
        setShowDuplicateWarning(false);
      }
    },
    [showDuplicateWarning],
  );

  const handleInputClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    },
    [],
  );

  if (!tab) return null;

  return (
    <div className="group relative flex items-center">
      <Tabs.Tab
        isSelected={isActive}
        onSelect={handleSelect}
        onClose={handleCloseTab}
        hasUnsavedChanges={tab.isLocalDirty}
        progress={progress}
        data-testid={`query-tab-${tabId}`}
        data-tab-id={tabId}
        data-tab-index={tabIndex}
        className={isEditing ? 'max-w-[400px]' : ''}
      >
        {isEditing ? (
          <span className="relative inline-block w-full max-w-[400px] overflow-visible">
            <span className="invisible whitespace-pre">
              {localValue || 'New Query'}
            </span>
            <input
              type="text"
              value={localValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              data-testid="query-tab-input"
              data-tab-id={tabId}
              data-tab-index={tabIndex}
              placeholder="Query name"
              className={`absolute inset-0 w-full rounded-md border-2 bg-transparent p-0 text-sm text-black transition-colors focus:outline-none dark:text-white ${
                showDuplicateWarning
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-transparent focus:border-blue-500'
              }`}
              autoFocus
            />
            {showDuplicateWarning && (
              <span className="absolute -bottom-5 left-0 text-xs text-red-500">
                A tab with this name already exists
              </span>
            )}
          </span>
        ) : (
          <span className={isActive ? 'cursor-text' : ''}>{tab?.name}</span>
        )}
      </Tabs.Tab>
    </div>
  );
}
